import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { SavedPrimitive } from '@features/fields/api/useSavePrimitives'
import { GuidancePrimitive } from 'api-validations/locutus/guidance'
import { useState } from 'react'
import {
  ButtonKind,
  CircleSpinner,
  Icon,
  MeatballMenu,
  MeatballMenuItem,
  PanoramaButton
} from 'shared-ui/components'
import { SpriteMap } from 'shared-ui/global'
import { useLocalization } from 'shared-ui/localization'
import { planZoneColor, useMapContext, useSelectedId } from 'shared-ui/map'
import { isNonNil } from 'utils'

import { GuidanceLineDrawing } from './GuidanceLineDrawing'

// One row in the plan's line list. `id` is the primitive asset id, which plans
// reference and the view-only layer keys the line's feature on.
export type LineRow = {
  id: string
  name: string
  type: GuidancePrimitive
}

export const PRIMITIVE_ICONS: Record<GuidancePrimitive, SpriteMap> = {
  [GuidancePrimitive.ABLine]: SpriteMap.GuidanceStraightIcon,
  [GuidancePrimitive.AHeading]: SpriteMap.GuidanceHeadingIcon,
  [GuidancePrimitive.Curve]: SpriteMap.GuidanceCurveIcon,
  [GuidancePrimitive.RadialPivot]: SpriteMap.GuidanceCircleIcon,
  [GuidancePrimitive.CurveDerivedPivot]: SpriteMap.GuidanceCurveIcon,
  [GuidancePrimitive.PrePropagated]: SpriteMap.GuidancePassesIcon
}

// Name-and-confirm step for a side picked on the map. Keyed by the picked
// side's id so switching sides resets the prefill.
const PickedSideNameForm = ({
  side,
  saving,
  onSave
}: {
  side: { id: string; label: string }
  saving: boolean
  onSave: (name: string) => void
}) => {
  const { t } = useLocalization()
  const [name, setName] = useState(side.label)
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-gray-200 p-3">
      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium text-gray-700">{t(($) => $.labels.name)}</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-green-500"
        />
      </label>
      <PanoramaButton
        type="button"
        kind={ButtonKind.Primary}
        disabledWhen={[
          { when: saving, reason: t(($) => $.disabledReasons.actionInProgress) },
          {
            when: name.trim().length === 0,
            reason: t(($) => $.disabledReasons.enterNameFirst)
          }
        ]}
        onClick={() => onSave(name.trim())}
      >
        {t(($) => $.planning.lineList.pickSideSave)}
      </PanoramaButton>
    </div>
  )
}

const LineListRow = ({
  line,
  color,
  removed,
  isDefault,
  isActive,
  previewLoading,
  activeLineId,
  onRemove,
  onRestore,
  onSelect
}: {
  line: LineRow
  color: string | undefined
  removed: boolean
  isDefault: boolean
  isActive: boolean
  previewLoading: boolean
  activeLineId: string | null
  onRemove: (id: string) => void
  onRestore: (id: string) => void
  onSelect: (id: string) => void
}) => {
  const { t } = useLocalization()
  const { map } = useMapContext()
  const selectedId = useSelectedId()
  const isSelected = selectedId === line.id
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: line.id
  })

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`mx-0.5 flex items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2 ${
        isActive
          ? 'bg-accent-50 border-accent-500 ring-2 ring-green-500'
          : isSelected
            ? 'border-gray-800'
            : 'border-gray-200'
      } ${removed ? 'opacity-50' : ''} ${isDragging ? 'relative z-10 shadow-md' : ''}`}
      onMouseEnter={() => map.drawTool.setSelected(line.id)}
      onMouseLeave={() => map.drawTool.setSelected(activeLineId)}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="shrink-0 cursor-grab touch-none select-none px-1 text-gray-400"
        aria-hidden
      >
        ≡
      </button>
      <button
        type="button"
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
        onClick={() => onSelect(line.id)}
      >
        {previewLoading ? (
          <CircleSpinner size="size-4" />
        ) : (
          <Icon
            icon={PRIMITIVE_ICONS[line.type]}
            className="size-4 shrink-0 text-gray-500"
            style={{ color: color }}
          />
        )}
        <span className="truncate text-sm font-medium text-gray-800">{line.name}</span>
        {isDefault && (
          <span className="shrink-0 rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-accent-700">
            {t(($) => $.planning.lineList.defaultChip)}
          </span>
        )}
        <span className="shrink-0 text-xs text-gray-400">
          {t(($) => $.planning.guidancePrimitiveLabels[line.type])}
        </span>
      </button>
      {removed ? (
        <button
          type="button"
          onClick={() => onRestore(line.id)}
          className="shrink-0 text-xs text-green-600 underline"
        >
          {t(($) => $.planning.lineList.restore)}
        </button>
      ) : (
        <MeatballMenu>
          <MeatballMenuItem
            color="danger"
            icon={<Icon icon={SpriteMap.TrashIcon} className="size-5" />}
            title={t(($) => $.planning.lineList.removeFromPlan)}
            onClick={() => onRemove(line.id)}
          />
        </MeatballMenu>
      )}
    </div>
  )
}

export const LineList = ({
  fieldId,
  lines,
  removedIds,
  defaultLineId,
  defaultLineName,
  onRemove,
  onRestore,
  onReorder,
  onSelect,
  activeLineId,
  isDrawing,
  onStartDrawing,
  onStopDrawing,
  onLineSaved,
  drawZoneId,
  hasSides,
  sidePicking,
  pickedSide,
  sideSaving,
  onStartSidePicking,
  onCancelSidePicking,
  onSavePickedSide,
  sidePreviewLoading
}: {
  fieldId: string
  lines: LineRow[]
  removedIds: ReadonlySet<string>
  // The row the remainder region follows.
  defaultLineId: string | null
  // Suggested (editable) name for a newly drawn line.
  defaultLineName: string
  onRemove: (id: string) => void
  onRestore: (id: string) => void
  // Move dragId into targetId's slot (arrayMove semantics).
  onReorder: (dragId: string, targetId: string) => void
  onSelect: (id: string) => void
  activeLineId?: string | null
  isDrawing: boolean
  onStartDrawing: () => void
  onStopDrawing: () => void
  onLineSaved: (saved: SavedPrimitive) => void
  // The zone id in-progress sketches are tagged with while drawing. Used to
  // clean the sketch out of the draw source when the user backs out.
  drawZoneId: string
  hasSides: boolean
  sidePicking: boolean
  pickedSide: { id: string; label: string } | null
  sideSaving: boolean
  onStartSidePicking: () => void
  onCancelSidePicking: () => void
  onSavePickedSide: (name: string) => void
  sidePreviewLoading: boolean
}) => {
  const { t } = useLocalization()
  const { map } = useMapContext()
  // A small activation distance keeps plain clicks (select, meatball) from
  // starting a drag.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over !== null && active.id !== over.id) onReorder(String(active.id), String(over.id))
  }

  if (isDrawing) {
    return (
      <GuidanceLineDrawing
        fieldId={fieldId}
        defaultName={defaultLineName}
        drawZoneId={drawZoneId}
        previewLoading={sidePreviewLoading}
        onBack={() => {
          const collection = map.drawTool.getFeatureCollection()
          collection.features
            .filter(
              (feat) =>
                (feat.geometry.type === 'LineString' || feat.geometry.type === 'Point') &&
                feat.properties?.zone === drawZoneId
            )
            .forEach((feat) => {
              if (isNonNil(feat.id)) map.drawTool.removeFeatureById(String(feat.id))
            })
          onStopDrawing()
        }}
        onSaved={onLineSaved}
      />
    )
  }

  if (sidePicking) {
    return (
      <div className="flex flex-col gap-3 py-4">
        <div className="pan-title4">{t(($) => $.planning.lineList.pickSide)}</div>
        <p className="text-sm text-gray-500">{t(($) => $.planning.lineList.pickSideHint)}</p>
        {pickedSide !== null && (
          <>
            {sidePreviewLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <CircleSpinner size="size-4" />
                <span>{t(($) => $.planning.lineList.previewLoading)}</span>
              </div>
            )}
            <PickedSideNameForm
              key={pickedSide.id}
              side={pickedSide}
              saving={sideSaving}
              onSave={onSavePickedSide}
            />
          </>
        )}
        <PanoramaButton
          type="button"
          kind={ButtonKind.Secondary}
          icon={SpriteMap.CloseCircleIcon}
          className="w-full"
          onClick={onCancelSidePicking}
        >
          {t(($) => $.buttons.cancel)}
        </PanoramaButton>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 py-4">
      <p className="text-sm text-gray-500">{t(($) => $.planning.lineList.description)}</p>
      <div className="flex items-center justify-between gap-2">
        <div className="pan-title4">
          {t(($) => $.planning.lines, { count: 2 })} (
          {lines.filter((l) => !removedIds.has(l.id)).length})
        </div>
        <div className="flex gap-2">
          {hasSides && (
            <PanoramaButton type="button" kind={ButtonKind.Secondary} onClick={onStartSidePicking}>
              {t(($) => $.planning.lineList.pickSide)}
            </PanoramaButton>
          )}
          <PanoramaButton
            type="button"
            kind={ButtonKind.Secondary}
            icon={SpriteMap.AddCircleIcon}
            iconLeft
            onClick={onStartDrawing}
          >
            {t(($) => $.buttons.new)}
          </PanoramaButton>
        </div>
      </div>

      {lines.length === 0 && (
        <div className="flex flex-col items-center gap-2 py-10 text-center">
          <Icon icon={SpriteMap.GuidanceCreatedLineIcon} className="size-8 text-gray-300" />
          <p className="text-sm text-gray-500">{t(($) => $.planning.lineList.empty)}</p>
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={lines.map((l) => l.id)} strategy={verticalListSortingStrategy}>
          <div className="flex flex-col gap-2">
            {(() => {
              let includedIndex = 0
              return lines.map((line) => {
                const removed = removedIds.has(line.id)
                const color = removed ? undefined : planZoneColor(includedIndex)
                if (!removed) includedIndex++
                const isActive = line.id === activeLineId
                return (
                  <LineListRow
                    key={line.id}
                    line={line}
                    color={color}
                    removed={removed}
                    isDefault={line.id === defaultLineId}
                    isActive={isActive}
                    previewLoading={isActive && sidePreviewLoading && !sidePicking}
                    activeLineId={activeLineId ?? null}
                    onRemove={onRemove}
                    onRestore={onRestore}
                    onSelect={onSelect}
                  />
                )
              })
            })()}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  )
}
